import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import { makeStyles } from 'tss-react/mui';
import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';

import type { LeaveEntry, LeaveTypeId, TeamMember } from '../data/model';
import { LEAVE_TYPES, startOfToday, toIso } from '../data/model';
import { fonts, themeTokens } from '../theme';

export interface NewLeaveInput {
  memberId: string;
  startDate: string;
  endDate: string;
  type: LeaveTypeId;
  note?: string;
}

const useStyles = makeStyles()(theme => {
  const palette = themeTokens(theme);
  return {
    paper: {
      backgroundColor: palette.surface,
      borderRadius: 14,
      border: `1px solid ${palette.border}`,
      width: '100%',
      maxWidth: 440,
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 24px 0',
    },
    title: {
      fontFamily: fonts.body,
      fontSize: 18,
      fontWeight: 700,
      color: palette.navy,
      letterSpacing: '-0.01em',
    },
    close: {
      border: 'none',
      background: 'transparent',
      color: palette.muted,
      cursor: 'pointer',
      display: 'inline-flex',
      padding: 4,
      borderRadius: 6,
      '&:hover': { color: palette.navy, backgroundColor: palette.paper },
    },
    form: {
      padding: '16px 24px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
    },
    field: { display: 'flex', flexDirection: 'column', gap: 6 },
    label: {
      fontFamily: fonts.mono,
      fontSize: 11,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: palette.muted,
    },
    input: {
      fontFamily: fonts.body,
      fontSize: 14,
      color: palette.navy,
      padding: '10px 12px',
      borderRadius: 8,
      border: `1px solid ${palette.border}`,
      backgroundColor: palette.surface,
      outline: 'none',
      transition: 'border-color 150ms ease',
      '&:focus': { borderColor: palette.navy },
    },
    row: { display: 'flex', gap: 12 },
    rowField: { flex: 1 },
    error: {
      fontFamily: fonts.mono,
      fontSize: 12,
      color: palette.terracotta,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 10,
      marginTop: 4,
    },
    cancel: {
      fontFamily: fonts.mono,
      fontSize: 12,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      padding: '10px 16px',
      borderRadius: 8,
      border: `1px solid ${palette.border}`,
      background: 'transparent',
      color: palette.navy,
      cursor: 'pointer',
      '&:hover': { borderColor: palette.borderStrong },
    },
    submit: {
      fontFamily: fonts.mono,
      fontSize: 12,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      padding: '10px 18px',
      borderRadius: 8,
      border: `1px solid ${palette.terracotta}`,
      backgroundColor: palette.terracotta,
      color: palette.onAccent,
      cursor: 'pointer',
      transition: 'opacity 150ms ease',
      '&:hover': { opacity: 0.9 },
    },
  };
});

export const AddLeaveModal = ({
  open,
  onClose,
  onSubmit,
  members,
  currentUserRef,
  editingLeave,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: NewLeaveInput) => void;
  members: TeamMember[];
  currentUserRef: string;
  editingLeave?: LeaveEntry | null;
}) => {
  const { classes } = useStyles();
  const todayIso = toIso(startOfToday());

  // Users may only request leave for themselves, so the member is fixed to the
  // signed-in user rather than being chosen from a dropdown.
  const currentMember = members.find(
    member =>
      member.id.toLocaleLowerCase('en-US') ===
      currentUserRef.toLocaleLowerCase('en-US'),
  );
  const memberId = currentMember?.id ?? currentUserRef;

  const [startDate, setStartDate] = useState(todayIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [type, setType] = useState<LeaveTypeId>(LEAVE_TYPES[0].id);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Sync the form with the leave being edited (or reset it for a new request)
  // whenever the dialog opens.
  useEffect(() => {
    if (!open) {
      return;
    }
    if (editingLeave) {
      setStartDate(editingLeave.startDate);
      setEndDate(editingLeave.endDate);
      setType(editingLeave.type);
      setNote(editingLeave.note ?? '');
    } else {
      setStartDate(todayIso);
      setEndDate(todayIso);
      setType(LEAVE_TYPES[0].id);
      setNote('');
    }
    setError(null);
  }, [open, editingLeave, todayIso]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!memberId) {
      setError('Could not determine your identity. Please try again.');
      return;
    }
    if (!startDate || !endDate) {
      setError('Please choose both a start and end date.');
      return;
    }
    if (endDate < startDate) {
      setError('End date cannot be before the start date.');
      return;
    }
    onSubmit({
      memberId,
      startDate,
      endDate,
      type,
      note: note.trim() ? note.trim() : undefined,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <span className={classes.title}>
          {editingLeave ? 'Edit Leave' : 'Add Leave'}
        </span>
        <button
          type="button"
          className={classes.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.field}>
          <label className={classes.label} htmlFor="tlt-member">
            Team member
          </label>
          <input
            id="tlt-member"
            type="text"
            className={classes.input}
            value={currentMember ? currentMember.name : 'You'}
            readOnly
            aria-readonly="true"
          />
        </div>

        <div className={classes.row}>
          <div className={`${classes.field} ${classes.rowField}`}>
            <label className={classes.label} htmlFor="tlt-start">
              Start date
            </label>
            <input
              id="tlt-start"
              type="date"
              className={classes.input}
              value={startDate}
              onChange={event => {
                setStartDate(event.target.value);
                setError(null);
              }}
            />
          </div>
          <div className={`${classes.field} ${classes.rowField}`}>
            <label className={classes.label} htmlFor="tlt-end">
              End date
            </label>
            <input
              id="tlt-end"
              type="date"
              className={classes.input}
              value={endDate}
              onChange={event => {
                setEndDate(event.target.value);
                setError(null);
              }}
            />
          </div>
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="tlt-type">
            Leave type
          </label>
          <select
            id="tlt-type"
            className={classes.input}
            value={type}
            onChange={event => setType(event.target.value as LeaveTypeId)}
          >
            {LEAVE_TYPES.map(leaveType => (
              <option key={leaveType.id} value={leaveType.id}>
                {leaveType.label}
              </option>
            ))}
          </select>
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="tlt-note">
            Note (optional)
          </label>
          <input
            id="tlt-note"
            type="text"
            className={classes.input}
            value={note}
            placeholder="Add an optional note"
            onChange={event => setNote(event.target.value)}
          />
        </div>

        {error && <div className={classes.error}>{error}</div>}

        <div className={classes.actions}>
          <button
            type="button"
            className={classes.cancel}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button type="submit" className={classes.submit}>
            {editingLeave ? 'Save changes' : 'Add leave'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};
